import { RefetchOptions, QueryObserverResult } from '@tanstack/react-query';
import { GreetingPhraseForm } from './GreetingPhraseForm';
import { User } from '@/types/TAccounts';
import { useEffect, useState } from 'react';
import { languageExamples } from 'src/constants/languageGreetings';
import DOMPurify from 'dompurify';
import CopyableText from '@/components/LabelWithdescription/CopyableText';
import { formatTextWithHighlightedVariables } from '@/utils/textFormatting';
import { Button } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { safeJSONParse } from '@/utils/helper';

export interface LanguageGreet {
	language: string;
	greeting: string;
}

export const GreetingPhraseInfo: React.FC<{
	data: any;
	refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<User, Error>>;
	isNoviUser?: boolean;
}> = ({ data, refetch, isNoviUser }) => {
	const systemPhrase = data?.system_phrase || '';
	const [languages, setLanguages] = useState<LanguageGreet[]>([]);
	const [isEditing, setIsEditing] = useState(false);

	useEffect(() => {
		if (data?.selected_languages) {
			const lan = safeJSONParse(data?.selected_languages, []);

			const lanWithoutEng = lan.filter((item) => item !== 'English');
			const lg =
				data?.language_greetings && data.language_greetings !== 'na'
					? safeJSONParse(data.language_greetings, {})
					: {};

			const lanWithoutEngGreet: LanguageGreet[] =
				lg &&
				lanWithoutEng.map((item: string) => {
					return {
						language: item,
						greeting: lg[item] || languageExamples[item as keyof typeof languageExamples],
					};
				});
			setLanguages(lanWithoutEngGreet);
		}
	}, [data?.selected_languages]);

	const sanitizeAndFormatText = (text: string) => {
		const sanitized = DOMPurify.sanitize(text);
		return sanitized.replace(/\n/g, '<br />');
	};

	const handleUpdateComplete = async () => {
		await refetch();
		setIsEditing(false);
	};

	const InfoComponent = () => {
		return (
			<div className="flex flex-col gap-4">
				{systemPhrase && (
					<div className="flex flex-col">
						<div className="flex items-center gap-1">
							<span className="font-medium">English</span>
							<CopyableText text={systemPhrase} hideText />
						</div>
						<span
							className="pl-4 w-9/12 mt-1 pt-2 pb-4"
							dangerouslySetInnerHTML={{
								__html: sanitizeAndFormatText(formatTextWithHighlightedVariables(systemPhrase)),
							}}
						/>
					</div>
				)}
				{languages?.length > 0 &&
					languages.map((item) => {
						return (
							<div key={item.language} className="flex flex-col">
								<div className="flex items-center gap-1">
									<span className="font-medium">{item.language}</span>
									<CopyableText text={item.greeting} hideText />
								</div>
								<span
									className="pl-4 w-9/12 mt-1 pt-2 pb-4"
									dangerouslySetInnerHTML={{ __html: sanitizeAndFormatText(item.greeting) }}
								/>
							</div>
						);
					})}
			</div>
		);
	};

	return (
		<div className="relative">
			<div className="flex justify-between items-start mb-4">
				<h3 className="text-lg font-medium">Greeting Phrases</h3>
				{!isEditing && (
					<Button
						icon={<EditOutlined />}
						onClick={() => setIsEditing(true)}
						className="border-blue-100 bg-blue-50 hover:bg-blue-100 hover:border-blue-200 text-blue-700"
						size="small"
					>
						Edit
					</Button>
				)}
			</div>

			{isEditing ? (
				<GreetingPhraseForm
					system_phrase={systemPhrase}
					refetch={handleUpdateComplete}
					languageGreets={languages}
					isNoviUser={isNoviUser}
				/>
			) : (
				<InfoComponent />
			)}
		</div>
	);
};
